/**
 * ConnectedAuditTrail Component
 *
 * Wrapper around AuditTrail that provides real-time Firestore updates.
 * Use this component in job pages for live audit trail data.
 *
 * Part of Phase 3: Submission Guidance & Review Tracking
 */

import { useAuditTrail } from '@/hooks/useAuditTrail';
import { AuditTrail } from './AuditTrail';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, Loader2 } from 'lucide-react';

interface ConnectedAuditTrailProps {
  jobId: string;
  className?: string;
  maxHeight?: string;
  showFilters?: boolean;
  showExport?: boolean;
  title?: string;
}

export function ConnectedAuditTrail({
  jobId,
  className,
  maxHeight = '500px',
  showFilters = true,
  showExport = true,
  title = 'Audit & Decision Trail',
}: ConnectedAuditTrailProps) {
  const { entries, isLoading, error } = useAuditTrail({ jobId });

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="text-lg">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span>Loading audit trail...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="text-lg">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground py-8">
            <Shield className="h-12 w-12 mx-auto mb-2 opacity-50 text-red-500" />
            <p className="text-red-500">{error}</p>
            <p className="text-sm mt-2">Please try refreshing the page.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <AuditTrail
      entries={entries}
      className={className}
      maxHeight={maxHeight}
      showFilters={showFilters}
      showExport={showExport}
      title={title}
    />
  );
}

export default ConnectedAuditTrail;
